import React, { useCallback, useLayoutEffect, useRef } from 'react';
import './text-area.scss';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /** Grow with the content instead of scrolling. `rows` is the minimum. */
  autoGrow?: boolean;
}

const grow = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + 2}px`;
};

/**
 * The multi-line counterpart of TextInput. forwardRef, as TextInput is.
 */
const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ invalid = false, autoGrow = false, className = '', onInput, value, ...rest }, ref) => {
    const inner = useRef<HTMLTextAreaElement | null>(null);

    const setRefs = useCallback(
      (el: HTMLTextAreaElement | null) => {
        inner.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      },
      [ref]
    );

    // Controlled value changes from outside (a refetch) re-measure too.
    useLayoutEffect(() => {
      if (autoGrow && inner.current) grow(inner.current);
    }, [autoGrow, value]);

    return (
      <textarea
        ref={setRefs}
        className={`text-area ${autoGrow ? 'text-area--auto' : ''} ${invalid ? 'is-invalid' : ''} ${className}`.trim()}
        aria-invalid={invalid || undefined}
        value={value}
        onInput={(e) => {
          if (autoGrow) grow(e.currentTarget);
          onInput?.(e);
        }}
        {...rest}
      />
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
